import React from 'react';
import {
  BarChart3, RefreshCw, Radio, Download, Bell, BellOff,
  Volume2, VolumeX, Tag as TagIcon, Home, DownloadCloud, DollarSign,
} from 'lucide-react';

/**
 * Kommo-style vertical sidebar for the cajero CRM.
 * Fixed 56px wide column with icon buttons and hover tooltips.
 * Renders only on desktop (md+).
 */
const IconButton = ({ icon: Icon, label, onClick, active = false, badge, testId, danger = false, accent = false, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className={`group relative w-10 h-10 flex items-center justify-center rounded-lg transition-colors
      ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
      ${active
            ? 'bg-indigo-50 text-indigo-600 shadow-sm dark:bg-indigo-500/15 dark:text-indigo-300'
          : danger
          ? 'text-slate-400 hover:text-red-600 hover:bg-red-50 dark:text-slate-400 dark:hover:text-red-300 dark:hover:bg-red-500/10'
          : accent
            ? 'text-indigo-500 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-500/15'
            : 'text-slate-500 hover:text-indigo-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-indigo-300 dark:hover:bg-slate-800'}
    `}
    aria-label={label}
    title={label}
  >
    <Icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
    {typeof badge === 'number' && badge > 0 && (
      <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center shadow-md">
        {badge > 99 ? '99+' : badge}
      </span>
    )}
    {/* Tooltip on hover */}
    <span className="pointer-events-none absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 rounded-md bg-slate-900 text-white text-[11px] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-50 border border-slate-800">
      {label}
    </span>
  </button>
);

const Divider = () => <div className="w-6 h-px bg-slate-200 dark:bg-slate-700/60" />;

export const SidebarNav = ({
  currentUser,
  onFunnelOpen,
  soundEnabled,
  onSoundToggle,
  notifyEnabled,
  onNotifyToggle,
  pwaPrompt,
  pwaInstalled,
  onInstall,
  onBroadcast,
  onTagsOpen,
  onMPInboxOpen,
  mpInboxUnread = 0,
  mpInboxTotalPending = 0,
  onRefresh,
  onContactsExport,
  unreadCount = 0,
  onBackToLines,
  showBackToLines = false,
}) => {
  const canBroadcast = currentUser?.role === 'admin' || (currentUser?.line_ids && currentUser.line_ids.length > 0);

  return (
    <aside
      className="hidden w-14 shrink-0 flex-col items-center gap-1 border-r border-slate-200 bg-white py-3 dark:border-slate-800/80 dark:bg-slate-900 md:flex"
      data-testid="cajero-sidebar-nav"
    >
      {showBackToLines && (
        <>
          <IconButton icon={Home} label="Volver a líneas" onClick={onBackToLines} testId="sidebar-back-to-lines" />
          <Divider />
        </>
      )}

      <IconButton
        icon={RefreshCw}
        label="Actualizar leads"
        onClick={onRefresh}
        testId="sidebar-refresh"
        badge={unreadCount}
      />

      <IconButton
        icon={BarChart3}
        label="Embudo de conversión"
        onClick={onFunnelOpen}
        testId="sidebar-funnel"
      />

      {canBroadcast && (
        <IconButton
          icon={Radio}
          label="Envío masivo (broadcast)"
          onClick={onBroadcast}
          testId="sidebar-broadcast"
          accent
        />
      )}

      {canBroadcast && (
        <IconButton
          icon={TagIcon}
          label="Gestionar etiquetas"
          onClick={onTagsOpen}
          testId="sidebar-tags"
          accent
        />
      )}

      {onMPInboxOpen && (
        <IconButton
          icon={DollarSign}
          label={
            mpInboxUnread > 0
              ? `${mpInboxUnread} pago${mpInboxUnread === 1 ? '' : 's'} nuevo${mpInboxUnread === 1 ? '' : 's'} de Mercado Pago`
              : mpInboxTotalPending > 0
                ? `${mpInboxTotalPending} pago${mpInboxTotalPending === 1 ? '' : 's'} sin asignar`
                : 'Pagos Mercado Pago'
          }
          onClick={onMPInboxOpen}
          testId="sidebar-mp-inbox"
          badge={mpInboxUnread || mpInboxTotalPending}
          active={mpInboxUnread > 0}
          accent
        />
      )}

      <Divider />

      <IconButton
        icon={soundEnabled ? Volume2 : VolumeX}
        label={soundEnabled ? 'Silenciar sonido' : 'Activar sonido de notificación'}
        onClick={onSoundToggle}
        active={soundEnabled}
        testId="sidebar-sound"
      />

      <IconButton
        icon={notifyEnabled ? Bell : BellOff}
        label={notifyEnabled ? 'Desactivar notificaciones' : 'Activar notificaciones'}
        onClick={onNotifyToggle}
        active={notifyEnabled}
        testId="sidebar-notify"
      />

      {onContactsExport && currentUser?.role === 'admin' && (
        <IconButton
          icon={Download}
          label="Exportar contactos"
          onClick={onContactsExport}
          testId="sidebar-export"
        />
      )}

      {pwaPrompt && !pwaInstalled && (
        <IconButton
          icon={DownloadCloud}
          label="Instalar app"
          onClick={onInstall}
          testId="sidebar-install"
          accent
        />
      )}

      <div className="flex-1" />

    </aside>
  );
};
